import { StackScreenProps } from "@react-navigation/stack";
import {
  Page,
  TextField,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SettingsRootParamList,
  ViewLlmStrategyRootScreenParamList,
} from "../../../../types";
import {
  useGlobalSettingsProvider,
  useMutationUpdateGlobalSettings,
} from "../../../../utilities/global-settings/hooks";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { DynamicMobileText } from "../../../../components/DynamicMobileText";
import { Colors } from "react-native-ui-lib";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { MUTATION_UPDATE_LLM_STRATEGY } from "../../common";
import { AiStrategy } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { ScreenContext } from "./common";

export interface GenericUpdateLlmConfigScreenProps
  extends StackScreenProps<
    ViewLlmStrategyRootScreenParamList,
    "GenericUpdateLlmConfigScreen"
  > {}
export const GenericUpdateLlmConfigScreen = ({
  navigation,
  route,
}: GenericUpdateLlmConfigScreenProps) => {
  const {
    defaultValue = "",
    title,
    arg,
    placeholder = "Enter a value",
    description,
  } = route.params;
  const { t } = useTranslation("settings");

  const [value, setValue] = useState(defaultValue || "");
  const { data, setStrategy } = React.useContext(ScreenContext);
  const aiStrategy = data?.strategy;
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    updateAiStrategy: AiStrategy;
  }>(MUTATION_UPDATE_LLM_STRATEGY);

  const shouldFocus = useHasTransitionEnded(navigation);

  const isDirty = useMemo(() => {
    return value != defaultValue;
  }, [value, defaultValue]);

  const isValid = useMemo(() => {
    return value.length > 0;
  }, [value]);

  const handleOnSubmit = async () => {
    const strategyCode = aiStrategy?.config?.code;

    const variables = {
      id: aiStrategy?.id!,
      options: {
        code: strategyCode,
        arguments: [
          {
            name: arg,
            value: value,
          },
        ],
      },
    };

    return m({
      variables: {
        input: variables,
      },
    })
      .then((result) => {
        if (result.data?.updateAiStrategy) {
          setStrategy(result.data.updateAiStrategy);
          flashMessagesContext.showMessage({
            message: "AI model updated successfully!",
            severity: "success",
          });
          navigation.goBack();
        }
      })
      .catch((error) => {
        console.error("Error creating LLM Strategy:", error);
      });
  };

  const footerMarkup = (
    <Button
      disabled={!isDirty || !isValid}
      label={t("button.update")}
      backgroundColor={Colors.action}
      color="white"
      onPress={async () => await handleOnSubmit()}
    ></Button>
  );

  return (
    <Page title={title} secondaryTitle={description} footer={footerMarkup}>
      <TextField
        placeholder={placeholder}
        value={value}
        onChangeText={(v) => setValue(v)}
        autoFocus={shouldFocus}
      />
    </Page>
  );
};
